type QuillDeltaOperation = {
  insert?: unknown;
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
};

type QuillDelta = {
  ops?: QuillDeltaOperation[];
};

type QuillMatcher = [number, (_node: unknown, delta: QuillDelta) => QuillDelta];

const ELEMENT_NODE = 1;
const ALLOWED_PASTED_FORMATS = new Set([
  'bold',
  'italic',
  'underline',
  'strike',
  'header',
  'list',
  'blockquote',
  'align',
  'link',
]);

function pickAllowedAttributes(attributes: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!attributes) return undefined;

  const allowed: Record<string, unknown> = {};
  Object.entries(attributes).forEach(([key, attrValue]) => {
    if (ALLOWED_PASTED_FORMATS.has(key)) {
      allowed[key] = attrValue;
    }
  });

  return Object.keys(allowed).length > 0 ? allowed : undefined;
}

function stripPastedFormatting(_node: unknown, delta: QuillDelta): QuillDelta {
  if (!Array.isArray(delta.ops)) return delta;

  delta.ops = delta.ops
    .filter((operation) => typeof operation.insert === 'string')
    .map((operation) => {
      const attributes = pickAllowedAttributes(operation.attributes);
      return attributes
        ? { insert: operation.insert, attributes }
        : { insert: operation.insert };
    });

  return delta;
}

const plainTextPasteMatchers: QuillMatcher[] = [
  [ELEMENT_NODE, stripPastedFormatting],
];

export function withPlainTextPaste<T extends Record<string, unknown>>(modules: T): T & { clipboard: Record<string, unknown> } {
  const existingClipboard = (
    modules.clipboard &&
    typeof modules.clipboard === 'object' &&
    !Array.isArray(modules.clipboard)
  )
    ? modules.clipboard as Record<string, unknown>
    : {};

  const existingMatchers = Array.isArray(existingClipboard.matchers)
    ? existingClipboard.matchers
    : [];

  return {
    ...modules,
    clipboard: {
      ...existingClipboard,
      matchVisual: false,
      matchers: [
        ...existingMatchers,
        ...plainTextPasteMatchers,
      ],
    },
  };
}

export const plainTextPasteModules = withPlainTextPaste({
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
});
